import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormHeader } from './FormHeader';
import { FormError } from './FormError';
import { SubmitButton } from './SubmitButton';
import { useForgotPasswordForm } from '../hooks/useForgotPasswordForm';

/*
 * No `required` asterisks on the auth surfaces — a deliberate design choice for
 * the signed-out experience, NOT a general "all fields required" rule.
 * Everywhere inside the application, required fields DO carry the asterisk,
 * whether or not the form also has optional fields.
 */
export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const { form, onSubmit, isSubmitting, isSuccess, submittedEmail, error, goToLogin } =
    useForgotPasswordForm();

  if (isSuccess) {
    return (
      <div className="w-full text-center">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-success/15 text-success">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h1 className="font-display text-[1.6rem] font-bold tracking-tight text-foreground">
          {t('auth.checkYourEmail')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('auth.resetLinkSent')}{' '}
          <strong className="text-foreground">{submittedEmail}</strong>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('auth.checkSpamFolder')}
        </p>
        <Button
          variant="outline"
          className="mt-6 w-full text-foreground"
          onClick={goToLogin}
        >
          {t('auth.backToLogin')}
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <FormHeader
        title={t('auth.forgotPasswordTitle')}
        subtitle={t('auth.forgotPasswordDescription')}
      />

      <FormError message={error} />

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="tenantCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.tenantCode')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auth.tenantCodePlaceholder')}
                    autoComplete="organization"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.emailAddress')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SubmitButton loading={isSubmitting}>
            {t('auth.sendResetLink')}
          </SubmitButton>

          <button
            type="button"
            onClick={goToLogin}
            className="mt-1 flex w-full items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            ← {t('auth.backToLogin')}
          </button>
        </form>
      </Form>
    </div>
  );
}
